import React from 'react';
import { Helmet } from 'react-helmet-async';

export const SEO = ({ title, description, keywords, url, faqSchema }) => {
  // Valores por defecto optimizados para intención de búsqueda local
  const defaultTitle = "Contadores en Corrientes Capital | R&M Estudio Contable";
  const defaultDescription =
    "Estudio contable en Corrientes Capital. Contadores públicos matriculados especialistas en asesoramiento impositivo, laboral, sueldos, monotributo y empresas.";
  const defaultKeywords =
    "Contadores en Corrientes Capital, Estudio Contable Corrientes, Contador Publico Corrientes, Asesoría Impositiva AFIP Rentas Corrientes, Liquidación de Sueldos Corrientes, Monotributo Corrientes, R&M Estudio Contable";
  const defaultUrl = "https://rymcontable.com.ar";

  const currentTitle = title ? `${title} | R&M Estudio Contable` : defaultTitle;
  const currentDescription = description || defaultDescription;
  const currentKeywords = keywords || defaultKeywords;
  const currentUrl = url || defaultUrl;

  const accountingServiceSchema = {
    "@context": "https://schema.org",
    "@type": ["AccountingService", "FinancialService", "ProfessionalService"],
    "@id": `${currentUrl}/#organization`,
    "name": "R&M Estudio Contable - Contadores en Corrientes Capital",
    "alternateName": "R&M Estudio Contable y Administrativo",
    "image": "https://rymcontable.com.ar/logo.png",
    "logo": "https://rymcontable.com.ar/logo.png",
    "description": currentDescription,
    "url": currentUrl,
    "telephone": "+543794223834",
    "priceRange": "$$",
    "currenciesAccepted": "ARS",
    "paymentAccepted": "Efectivo, Transferencia Bancaria, Débito, Crédito",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Corrientes",
      "addressRegion": "Corrientes",
      "postalCode": "3400",
      "addressCountry": "AR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": -27.4692131,
      "longitude": -58.8335011
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "Corrientes"
      },
      {
        "@type": "AdministrativeArea",
        "name": "Provincia de Corrientes"
      },
      {
        "@type": "City",
        "name": "Resistencia"
      }
    ],
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "18:00"
      }
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Servicios Contables y Fiscales",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Asesoramiento Impositivo AFIP y Rentas DGR Corrientes"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Liquidación de Sueldos y Cargas Sociales en Corrientes"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Gestión Integral de Monotributo y Responsables Inscriptos"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Auditorías, Balances y Certificaciones Contables"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Constitución y Asesoría Legal para Empresas y PyMEs"
          }
        }
      ]
    },
    "sameAs": [
      "https://instagram.com/contadorasrym",
      "https://www.facebook.com"
    ]
  };

  const faqPageSchema =
    faqSchema && faqSchema.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqSchema.map((item) => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.answer
            }
          }))
        }
      : null;

  return (
    <Helmet>
      {/* Título y Meta Básicos */}
      <title>{currentTitle}</title>
      <meta name="description" content={currentDescription} />
      <meta name="keywords" content={currentKeywords} />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <link rel="canonical" href={currentUrl} />

      {/* Geolocalización Local */}
      <meta name="geo.region" content="AR-W" />
      <meta name="geo.placename" content="Corrientes, Argentina" />
      <meta name="geo.position" content="-27.4692131;-58.8335011" />
      <meta name="ICBM" content="-27.4692131, -58.8335011" />

      {/* Open Graph / Facebook / WhatsApp */}
      <meta property="og:locale" content="es_AR" />
      <meta property="og:type" content="business.business" />
      <meta property="og:site_name" content="R&M Estudio Contable" />
      <meta property="og:title" content={currentTitle} />
      <meta property="og:description" content={currentDescription} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content="https://rymcontable.com.ar/og-image.png" />
      <meta property="og:image:alt" content="R&M Estudio Contable en Corrientes Capital" />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={currentTitle} />
      <meta name="twitter:description" content={currentDescription} />
      <meta name="twitter:image" content="https://rymcontable.com.ar/og-image.png" />

      {/* Datos Estructurados JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(accountingServiceSchema)}
      </script>

      {faqPageSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqPageSchema)}
        </script>
      )}
    </Helmet>
  );
};
